import { Check, Search, User, X } from 'lucide-react'
import { useState, useMemo } from 'react'

import { Badge } from '@/components/ui/badge'
import { useProjectMembers, type DeveloperGroup, type ProjectMember } from '@/hooks'
import { cn } from '@/lib/utils'

// ==================== Types ====================

export interface SelectedAssignee {
  id: string
  name: string
  role: 'owner' | 'participant'
}

interface AssigneeSelectorProps {
  projectId: string
  mode: 'single' | 'multiple'
  value: string | string[]
  onChange: (value: string | string[]) => void
  placeholder?: string
  disabled?: boolean
  excludeIds?: string[]
  filterGroup?: DeveloperGroup
}

interface MemberOption {
  id: string
  employeeId: string
  name: string
  developerGroup: DeveloperGroup | null
}

// ==================== Constants ====================

const GROUP_LABELS: Record<DeveloperGroup, string> = {
  product: '产品',
  design: '设计',
  frontend: '前端',
  backend: '后端',
  testing: '测试',
}

// ==================== Component ====================

/**
 * 人员选择器组件
 * - 支持单选（owner）和多选（participants）模式
 * - 支持按开发组筛选
 * - 支持搜索功能
 */
export function AssigneeSelector({
  projectId,
  mode,
  value,
  onChange,
  placeholder = '选择人员',
  disabled = false,
  excludeIds = [],
  filterGroup,
}: AssigneeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedGroup, setSelectedGroup] = useState<DeveloperGroup | 'all'>(filterGroup || 'all')

  const { data: members, isLoading } = useProjectMembers(projectId)

  // 将项目成员转换为选项列表
  const memberOptions = useMemo<MemberOption[]>(() => {
    if (!members) {return []}

    const options: MemberOption[] = []

    // 添加开发者成员
    Object.entries(members.developers).forEach(([group, groupMembers]) => {
      groupMembers.forEach((member: ProjectMember) => {
        options.push({
          id: member.id,
          employeeId: member.employeeId,
          name: member.employee?.name || `员工 ${member.employeeId.slice(0, 8)}`,
          developerGroup: group as DeveloperGroup,
        })
      })
    })

    return options
  }, [members])

  // 筛选后的选项
  const filteredOptions = useMemo(() => {
    return memberOptions.filter((option) => {
      // 排除已选中的（多选模式下）
      if (excludeIds.includes(option.employeeId)) {return false}

      // 按开发组筛选
      if (selectedGroup !== 'all' && option.developerGroup !== selectedGroup) {return false}

      // 搜索筛选
      if (search && !option.name.toLowerCase().includes(search.toLowerCase())) {return false}

      return true
    })
  }, [memberOptions, excludeIds, selectedGroup, search])

  // 当前选中的值
  const selectedValues = useMemo(() => {
    if (mode === 'single') {
      return value ? [value as string] : []
    }
    return (value as string[]) || []
  }, [mode, value])

  // 获取选中项的显示名称
  const getSelectedNames = () => {
    return selectedValues
      .map((id) => memberOptions.find((o) => o.employeeId === id)?.name || id.slice(0, 8))
      .join(', ')
  }

  // 处理选择
  const handleSelect = (employeeId: string) => {
    if (mode === 'single') {
      onChange(employeeId)
      setIsOpen(false)
    } else {
      const currentValues = (value as string[]) || []
      if (currentValues.includes(employeeId)) {
        onChange(currentValues.filter((id) => id !== employeeId))
      } else {
        onChange([...currentValues, employeeId])
      }
    }
  }

  // 移除选中项
  const handleRemove = (employeeId: string) => {
    if (mode === 'single') {
      onChange('')
    } else {
      const currentValues = (value as string[]) || []
      onChange(currentValues.filter((id) => id !== employeeId))
    }
  }

  // 清空选择
  const handleClear = () => {
    if (mode === 'single') {
      onChange('')
    } else {
      onChange([])
    }
  }

  return (
    <div className="relative">
      {/* 触发器 */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex min-h-[38px] w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-primary',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <div className="flex flex-1 flex-wrap gap-1">
          {selectedValues.length > 0 ? (
            mode === 'single' ? (
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {getSelectedNames()}
              </span>
            ) : (
              selectedValues.map((id) => {
                const option = memberOptions.find((o) => o.employeeId === id)
                return (
                  <Badge key={id} variant="secondary" className="gap-1">
                    {option?.name || id.slice(0, 8)}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemove(id)
                      }}
                    />
                  </Badge>
                )
              })
            )
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </div>
        {selectedValues.length > 0 && (
          <X
            className="ml-2 h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
          />
        )}
      </button>

      {/* 下拉面板 */}
      {isOpen && (
        <>
          {/* 遮罩层 */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* 下拉内容 */}
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
            {/* 搜索框 */}
            <div className="border-b p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜索人员..."
                  className="w-full rounded-md border bg-background py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* 开发组筛选 */}
            <div className="flex flex-wrap gap-1 border-b p-2">
              <Badge
                variant={selectedGroup === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedGroup('all')}
              >
                全部
              </Badge>
              {Object.entries(GROUP_LABELS).map(([group, label]) => (
                <Badge
                  key={group}
                  variant={selectedGroup === group ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSelectedGroup(group as DeveloperGroup)}
                >
                  {label}
                </Badge>
              ))}
            </div>

            {/* 选项列表 */}
            <div className="max-h-60 overflow-y-auto p-1">
              {isLoading ? (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  加载中...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="py-4 text-center text-sm text-muted-foreground">
                  没有找到匹配的人员
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.employeeId)
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleSelect(option.employeeId)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm',
                        'hover:bg-accent',
                        isSelected && 'bg-accent'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{option.name}</span>
                        {option.developerGroup && (
                          <Badge variant="outline" className="text-xs">
                            {GROUP_LABELS[option.developerGroup]}
                          </Badge>
                        )}
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  )
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
